import { useEffect, useRef } from 'react';
import type { ApiClient } from '../api';
import { BRAND_META } from '../brand';
import { compact, money } from '../format';
import { usePoll } from '../hooks';
import type { MoneyByCurrency, TickerItem } from '../types';

/**
 * The ticker strip (Matthew, 1 October 2026: "a scrolling ticker like on
 * stock exchanges"). Running totals across the brands, each with today's
 * change, scrolling under the header.
 *
 * Every line says what kind of number it is, because the three are not the
 * same claim (see shell/api/src/ticker.ts):
 *
 *  - a platform total (snapshot) shows plainly: it is the real figure;
 *  - a tracked total carries "since 29 Sep", because counting from the day
 *    the bus went live is not the platform's total and must not pass for it;
 *  - a source that has sent nothing says "awaiting data" rather than 0.
 *
 * Motion stops on hover and keyboard focus, and for anyone who asks the
 * system for reduced motion the strip becomes a row they scroll themselves.
 *
 * Swipe (Matthew, 1 November 2026: "move the ticker across quicker by
 * gently swiping it to see what's next or what I've just missed but it
 * should continue as normal otherwise"): a drag moves the strip with the
 * finger either way, a flick carries on and slows to a stop, then the
 * normal crawl resumes. Vertical page scrolling is left to the browser.
 * The second copy that makes the loop seamless is hidden from assistive
 * technology, so a screen reader reads each line once.
 */

const PX_PER_SECOND = 45;

/** Today's change for a line: "▲3", "▲£12.40", "▼£4.00", or nothing. */
export function changeLabel(item: TickerItem): { text: string; dir: 'up' | 'down' } | null {
  if (item.today === null) return null;
  if (item.unit === 'count') {
    return item.today > 0 ? { text: `▲${compact(item.today)}`, dir: 'up' } : null;
  }
  const entries = Object.entries(item.today).filter(([, minor]) => minor !== 0);
  if (entries.length === 0) return null;
  const down = entries.every(([, minor]) => minor < 0);
  const text = entries
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .map(([cur, minor]) => `${minor < 0 ? '▼' : '▲'}${money(Math.abs(minor), cur)}`)
    .join(' ');
  return { text, dir: down ? 'down' : 'up' };
}

/**
 * A line's value: compact counts, money per currency, never blended.
 * A currency at zero is left out while another has money in it: CashBlack's
 * snapshot names GBP and USD every time, zero included, and "£842.00 +
 * US$0.00" says nothing the first half did not.
 */
export function valueLabel(item: TickerItem): string {
  if (item.total === null) return '–';
  if (item.unit === 'count') return compact(item.total);
  const all = Object.entries(item.total as MoneyByCurrency);
  const entries = all.filter(([, minor]) => minor !== 0).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return money(0, all.some(([cur]) => cur === 'GBP') || all.length === 0 ? 'GBP' : all[0]![0]);
  return entries.map(([cur, minor]) => money(minor, cur)).join(' + ');
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });

/** The basis in words, for the hover title and the screen reader. */
export function basisNote(item: TickerItem): string {
  if (item.note) return item.note;
  if (item.basis === 'awaiting') return 'No data from this source yet.';
  if (item.basis === 'tracked') {
    return `Counted from live events since ${item.since ? shortDate(item.since) : 'tracking began'}. The platform's own total has not arrived yet.`;
  }
  const at = item.since
    ? new Date(item.since).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/London',
      })
    : 'its last count';
  return `The platform's total as of ${at}, plus live events since.`;
}

function Line({ item }: { item: TickerItem }) {
  const change = changeLabel(item);
  const colour = item.brand ? BRAND_META[item.brand].series : 'var(--cc-teal)';
  return (
    <li className={`cc-tick${item.basis === 'awaiting' ? ' awaiting' : ''}`} title={basisNote(item)}>
      <span className="cc-tick-dot" style={{ background: colour }} aria-hidden />
      <span className="cc-tick-label">{item.label}</span>
      <span className="cc-tick-value">{valueLabel(item)}</span>
      {change && <span className={`cc-tick-change ${change.dir}`}>{change.text}</span>}
      {item.basis === 'tracked' && item.since && (
        <span className="cc-tick-since">since {shortDate(item.since)}</span>
      )}
      {item.basis === 'awaiting' && <span className="cc-tick-since">awaiting data</span>}
    </li>
  );
}

/** Wraps an offset into [0, loop): the strip is two copies, so any offset is a seam-free frame. */
export function wrapOffset(offset: number, loop: number): number {
  if (loop <= 0) return 0;
  return ((offset % loop) + loop) % loop;
}

/** A flick's speed after `ms` of friction, in px per second. */
export function decay(velocity: number, ms: number): number {
  const v = velocity * Math.exp(-ms / 325);
  return Math.abs(v) < 5 ? 0 : v;
}

/**
 * Drives the strip: the steady crawl, a finger dragging it, and the glide
 * after a flick. Everything lives in refs and one animation frame loop,
 * so a swipe never re-renders the lines.
 */
function useTickerMotion(
  trackRef: React.RefObject<HTMLDivElement>,
  listRef: React.RefObject<HTMLUListElement>,
  mounted: boolean,
) {
  useEffect(() => {
    const track = trackRef.current;
    const list = listRef.current;
    if (!track || !list) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let offset = 0;
    let glide = 0; // px/s left over from a flick
    let dragging = false;
    let hovering = false;
    let focused = false;
    let lastX = 0;
    let lastMoveAt = 0;
    let fling = 0;
    let startX = 0;
    let startY = 0;
    let decided: 'x' | 'y' | null = null;
    let frame = 0;
    let prev = performance.now();

    const paint = () => {
      track.style.transform = `translate3d(${-wrapOffset(offset, list.offsetWidth)}px,0,0)`;
    };

    const tick = (now: number) => {
      const dt = Math.min(64, now - prev);
      prev = now;
      if (!dragging) {
        if (glide !== 0) {
          offset += (glide * dt) / 1000;
          glide = decay(glide, dt);
        } else if (!hovering && !focused) {
          offset += (PX_PER_SECOND * dt) / 1000;
        }
        paint();
      }
      frame = requestAnimationFrame(tick);
    };

    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      decided = e.pointerType === 'mouse' ? 'x' : null;
      glide = 0;
      fling = 0;
      startX = lastX = e.clientX;
      startY = e.clientY;
      lastMoveAt = performance.now();
    };
    const move = (e: PointerEvent) => {
      if (!dragging) return;
      if (!decided) {
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        if (dx < 6 && dy < 6) return;
        decided = dx > dy ? 'x' : 'y';
        if (decided === 'y') {
          dragging = false; // a page scroll, not a swipe
          return;
        }
        track.setPointerCapture?.(e.pointerId);
      }
      const now = performance.now();
      const dx = e.clientX - lastX;
      offset -= dx;
      const dt = Math.max(1, now - lastMoveAt);
      fling = 0.8 * ((-dx * 1000) / dt) + 0.2 * fling;
      lastX = e.clientX;
      lastMoveAt = now;
      paint();
    };
    const up = () => {
      if (!dragging) return;
      dragging = false;
      // Only a flick that was still moving at release glides on.
      glide = performance.now() - lastMoveAt < 80 ? Math.max(-4000, Math.min(4000, fling)) : 0;
    };
    const enter = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') hovering = true;
    };
    const leave = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') hovering = false;
    };
    const fin = () => (focused = true);
    const fout = () => (focused = false);

    track.addEventListener('pointerdown', down);
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);
    track.addEventListener('pointerenter', enter);
    track.addEventListener('pointerleave', leave);
    track.addEventListener('focusin', fin);
    track.addEventListener('focusout', fout);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener('pointerdown', down);
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
      track.removeEventListener('pointercancel', up);
      track.removeEventListener('pointerenter', enter);
      track.removeEventListener('pointerleave', leave);
      track.removeEventListener('focusin', fin);
      track.removeEventListener('focusout', fout);
    };
  }, [trackRef, listRef, mounted]);
}

export function Ticker({ api }: { api: ApiClient }) {
  const ticker = usePoll<{ items: TickerItem[] }>(() => api.get('/api/ticker'), 60_000, ['ticker']);
  const items = ticker.data?.items ?? [];
  const trackRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const hasItems = items.length > 0;
  useTickerMotion(trackRef, listRef, hasItems);
  if (!hasItems) return null;

  return (
    <section className="cc-ticker" aria-label="Running totals">
      <div className="cc-ticker-track" ref={trackRef} tabIndex={0}>
        <ul className="cc-ticker-list" ref={listRef}>
          {items.map((item) => (
            <Line key={item.key} item={item} />
          ))}
        </ul>
        <ul className="cc-ticker-list" aria-hidden>
          {items.map((item) => (
            <Line key={item.key} item={item} />
          ))}
        </ul>
      </div>
    </section>
  );
}
